import { resolveEffectiveAnswerForSubitems } from './unansweredValue';
import { Question, OASISSchema, OASISFormData, FormValue } from './types';

/**
 * Checks if a value is meaningful (not empty).
 */
const hasValue = (value: FormValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
};

/**
 * Returns subitem questions revealed by the parent's current answer value.
 * Matches the subitem visibility rules used by `OASISQuestionRenderer`.
 */
export const getSelectedSubitems = (
  question: Question,
  value: FormValue | undefined
): Question[] => {
  if (!question.subitems) return [];

  const effectiveValue = resolveEffectiveAnswerForSubitems(question, value);
  const selectedValues =
    typeof effectiveValue === 'string'
      ? [effectiveValue]
      : Array.isArray(effectiveValue)
        ? effectiveValue.filter(
            (item): item is string => typeof item === 'string'
          )
        : [];

  const seenQids = new Set<string>();
  const selectedSubitems: Question[] = [];

  selectedValues.forEach(selectedValue => {
    (question.subitems?.[selectedValue] ?? []).forEach(subitem => {
      if (seenQids.has(subitem.qid)) return;
      seenQids.add(subitem.qid);
      selectedSubitems.push(subitem);
    });
  });

  return selectedSubitems;
};

/**
 * Extracts OASIS form data for persistence or validation.
 * Returns sparse QID-based data: only questions with meaningful answers.
 *
 * @param formData The complete form data
 * @param schema The OASIS schema containing all questions
 * @returns Form data with QIDs as keys
 */
export const getOASISFormData = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = {};

  // Process all questions recursively
  const processQuestion = (question: Question) => {
    const value = formData[question.qid];

    if (hasValue(value)) {
      result[question.qid] = value;
    }

    // Process subitems for the currently selected value
    if (question.subitems) {
      getSelectedSubitems(question, value).forEach(subitem =>
        processQuestion(subitem)
      );
    }
  };

  // Process all categories
  Object.values(schema).forEach(questions => {
    questions.forEach(question => processQuestion(question));
  });

  return result;
};

/**
 * Clears subitem data when a parent question's value changes.
 * Returns an object with subitem keys to be cleared (set to undefined).
 *
 * @param question The parent question that changed
 * @param newValue The new value selected for the parent question
 * @param currentFormData Current form data to check for existing subitem values
 * @returns Object with subitem keys that should be cleared
 */
export const getSubitemClearUpdates = (
  question: Question,
  newValue: FormValue,
  currentFormData: OASISFormData
): Partial<OASISFormData> => {
  const clearUpdates: Partial<OASISFormData> = {};

  if (!question.subitems) {
    return clearUpdates;
  }

  // Collect all subitem QIDs recursively
  const collectAllQids = (q: Question, qids: Set<string>) => {
    if (q.subitems) {
      Object.values(q.subitems).forEach(items => {
        items.forEach(item => {
          qids.add(item.qid);
          collectAllQids(item, qids);
        });
      });
    }
  };

  // Get all possible subitem QIDs
  const allQids = new Set<string>();
  collectAllQids(question, allQids);

  // Get QIDs that should remain active (for the new value)
  const activeQids = new Set<string>();
  getSelectedSubitems(question, newValue).forEach(subitem => {
    activeQids.add(subitem.qid);
    collectAllQids(subitem, activeQids);
  });

  // Clear inactive subitems that have values
  allQids.forEach(qid => {
    if (!activeQids.has(qid) && currentFormData[qid] !== undefined) {
      clearUpdates[qid] = undefined;
    }
  });

  return clearUpdates;
};
